import { useMemo } from 'react';
import { LAYOUT_STORAGE_VERSION } from '../../domain';
import type { PersistedLayout, ResolvedColumn, SubTable } from '../../domain';
import type { TableAdapter } from '../../ports';
import { resolveAllColumns, resolveColumns } from '../columns/use-columns';

const EMPTY_LAYOUT: PersistedLayout = { v: LAYOUT_STORAGE_VERSION };

export interface ControlTarget {
    adapter: TableAdapter<unknown>;
    columns: ResolvedColumn<unknown>[];
    allColumns: ResolvedColumn<unknown>[];
}

export interface UseControlTargetArgs<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    allColumns?: ResolvedColumn<T>[];
    // eslint-disable-next-line typescript/no-explicit-any -- see TableProps.subTable for why `any`, not `unknown`
    subTable?: SubTable<T, any>;
    childAdapter: TableAdapter<unknown> | null;
    expanded: boolean;
}

export function useControlTarget<T>(
    args: UseControlTargetArgs<T>,
): ControlTarget {
    const { adapter, columns, allColumns, subTable, childAdapter, expanded } =
        args;

    const childAllColumns = useMemo(
        () =>
            subTable
                ? resolveAllColumns(
                      subTable.columns,
                      childAdapter?.getSnapshot().capabilities ?? {},
                  )
                : [],
        [subTable, childAdapter],
    );

    const childColumns = useMemo(
        () =>
            subTable
                ? resolveColumns({
                      defs: subTable.columns,
                      caps: childAdapter?.getSnapshot().capabilities ?? {},
                      layout: EMPTY_LAYOUT,
                  })
                : [],
        [subTable, childAdapter],
    );

    if (expanded && childAdapter) {
        return {
            adapter: childAdapter,
            columns: childColumns,
            allColumns: childAllColumns,
        };
    }
    return {
        adapter,
        columns: columns as unknown as ResolvedColumn<unknown>[],
        allColumns: (allColumns ??
            columns) as unknown as ResolvedColumn<unknown>[],
    };
}
